// Integrations — per-tenant credit-report acquisition APIs.
//
// This is where a company connects the paid services that pull a client's
// credit file: iSoftpull (no-score-impact soft pull) and the SmartCredit /
// ConsumerDirect Partner API. Credentials are stored encrypted server-side
// (pgcrypto) and are never read back — the page only ever shows a Connected /
// Not connected status and lets the operator run a live "Test connection"
// against each provider's sandbox. Per-client monitoring logins (MFSN,
// IdentityIQ, MyScoreIQ) live on the client record; this page shows their
// availability as a status board.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2, AlertCircle, Landmark, ShieldCheck, FileText, Camera, ScanLine,
} from "lucide-react";
import { Shell } from "../components/Shell";
import { company as companyApi, integrations, type CompanySettings } from "../lib/api";

type Status = "connected" | "action" | "off";

function StatusRow({ icon: Icon, title, detail, status }: {
  icon: any; title: string; detail: string; status: Status;
}) {
  const pill =
    status === "connected"
      ? { cls: "status-pill active", txt: "Connected", ic: <CheckCircle2 size={13} aria-hidden="true" /> }
      : status === "action"
      ? { cls: "status-pill warning", txt: "Needs setup", ic: <AlertCircle size={13} aria-hidden="true" /> }
      : { cls: "status-pill pending", txt: "Optional", ic: null };
  return (
    <div className="table-row table-row--wide" style={{ alignItems: "flex-start", gap: 12 }}>
      <Icon size={20} aria-hidden="true" style={{ color: "var(--ne-chrome-2)", flex: "0 0 auto", marginTop: 2 }} />
      <div style={{ flex: "1 1 300px", minWidth: 0 }}>
        <strong>{title}</strong>
        <p className="muted small" style={{ margin: "3px 0 0" }}>{detail}</p>
      </div>
      <span className={pill.cls} style={{ display: "inline-flex", gap: 5, alignItems: "center" }}>
        {pill.ic}{pill.txt}
      </span>
    </div>
  );
}

export default function IntegrationsPage() {
  const nav = useNavigate();
  const [s, setS] = useState<CompanySettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // iSoftpull soft-pull
  const [spKey, setSpKey] = useState("");
  const [spSecret, setSpSecret] = useState("");
  const [spBusy, setSpBusy] = useState(false);
  const [spMsg, setSpMsg] = useState<string | null>(null);

  // SmartCredit / ConsumerDirect Partner API
  const [scId, setScId] = useState("");
  const [scSecret, setScSecret] = useState("");
  const [scEnv, setScEnv] = useState<"production" | "stage">("production");
  const [scBusy, setScBusy] = useState(false);
  const [scMsg, setScMsg] = useState<string | null>(null);

  async function reload() {
    const r = await companyApi.get();
    setS(r.settings || null);
    if (r.settings?.smartcredit_api_env === "stage") setScEnv("stage");
  }

  useEffect(() => {
    (async () => {
      try { await reload(); }
      catch (e: any) { setErr(e?.message || "Could not load integrations."); }
      finally { setLoading(false); }
    })();
  }, []);

  async function saveSoftpull() {
    setSpBusy(true); setSpMsg(null);
    try {
      // company PUT nulls unlisted branding fields by design — echo the full
      // current settings back with only the new (non-empty) secrets layered on.
      await companyApi.update({
        ...(s || {}),
        ...(spKey.trim() ? { softpull_api_key: spKey.trim() } : {}),
        ...(spSecret.trim() ? { softpull_api_secret: spSecret.trim() } : {}),
      } as any);
      await reload();
      setSpKey(""); setSpSecret("");
      setSpMsg("Saved. Click Test connection to verify the keys work.");
    } catch (e: any) { setSpMsg(e?.message || "Could not save."); }
    finally { setSpBusy(false); }
  }

  async function testSoftpull() {
    setSpBusy(true); setSpMsg(null);
    try {
      const r = await integrations.testSoftpull();
      const found = Object.entries(r.scores || {});
      const scoreTxt = found.length ? ` Scores: ${found.map(([m, v]) => `${m} ${v}`).join(", ")}.` : "";
      setSpMsg(`Connected — sandbox pull for ${r.test_applicant} came back.${scoreTxt}`);
    } catch (e: any) { setSpMsg(`Test failed: ${e?.message || "iSoftpull rejected the request."}`); }
    finally { setSpBusy(false); }
  }

  async function saveSmartCredit() {
    setScBusy(true); setScMsg(null);
    try {
      await companyApi.update({
        ...(s || {}),
        smartcredit_api_env: scEnv,
        ...(scId.trim() ? { smartcredit_client_id: scId.trim() } : {}),
        ...(scSecret.trim() ? { smartcredit_client_secret: scSecret.trim() } : {}),
      } as any);
      await reload();
      setScId(""); setScSecret("");
      setScMsg("Saved. Click Test connection to verify it works.");
    } catch (e: any) { setScMsg(e?.message || "Could not save."); }
    finally { setScBusy(false); }
  }

  async function testSmartCredit() {
    setScBusy(true); setScMsg(null);
    try {
      const r = await integrations.testSmartcredit();
      setScMsg(`Connected (${r.environment}) — ${r.message}`);
    } catch (e: any) { setScMsg(`Test failed: ${e?.message || "SmartCredit rejected the request."}`); }
    finally { setScBusy(false); }
  }

  const softpullOn = !!s?.softpull_configured;
  const scApiOn = !!s?.smartcredit_api_configured;

  return (
    <Shell title="Integrations" subtitle="Credit-report pulls & partner APIs">
      {loading && <p className="muted small">Loading…</p>}
      {err && <p className="error-text">{err}</p>}

      {!loading && !err && (
        <>
          {/* iSoftpull soft-pull */}
          <div className="panel" style={{ marginBottom: 16 }}>
            <div className="panel-header">
              <div>
                <h2>iSoftpull — soft-pull prequalification</h2>
                <span className="muted small">
                  A no-score-impact bureau pull from name + address only — no client login needed.
                  Run it from a client's record once they've signed the service agreement.
                </span>
              </div>
              <span className={`status-pill ${softpullOn ? "active" : "pending"}`}>
                {softpullOn ? "Connected" : "Not connected"}
              </span>
            </div>
            <div className="form-row-2">
              <label className="form-field">
                <span>API Key ID</span>
                <input type="password" value={spKey} onChange={(e) => setSpKey(e.target.value)}
                  placeholder={softpullOn ? "•••••• saved — paste to replace" : "API Key ID from iSoftpull"} autoComplete="off" />
              </label>
              <label className="form-field">
                <span>Secret Access Token</span>
                <input type="password" value={spSecret} onChange={(e) => setSpSecret(e.target.value)}
                  placeholder={softpullOn ? "•••••• saved — paste to replace" : "Secret Access Token from iSoftpull"} autoComplete="off" />
              </label>
            </div>
            <p className="muted small" style={{ margin: "8px 0 10px" }}>
              Keys are stored encrypted and only ever used for your company. Test runs iSoftpull's
              documented sandbox applicant (Steve Johnson) — it costs nothing and proves the keys before a real client pull.
            </p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="primary-button" disabled={spBusy || (!spKey.trim() && !spSecret.trim())} onClick={saveSoftpull}>
                {spBusy ? "Working…" : "Save"}
              </button>
              <button className="ghost-button" disabled={spBusy || !softpullOn} onClick={testSoftpull}>
                Test connection
              </button>
            </div>
            {spMsg && <p className="muted small" style={{ marginTop: 10 }}>{spMsg}</p>}
          </div>

          {/* SmartCredit / ConsumerDirect Partner API */}
          <div className="panel" style={{ marginBottom: 16 }}>
            <div className="panel-header">
              <div>
                <h2>SmartCredit API (ConsumerDirect)</h2>
                <span className="muted small">
                  Your ConsumerDirect Partner API key. When connected, SmartCredit reports import through the
                  official API — no client login, and it bypasses the bot wall that blocks the browser scrape.
                </span>
              </div>
              <span className={`status-pill ${scApiOn ? "active" : "pending"}`}>
                {scApiOn ? `Connected (${s?.smartcredit_api_env || "production"})` : "Not connected"}
              </span>
            </div>
            <div className="form-row-2">
              <label className="form-field">
                <span>Client ID</span>
                <input type="password" value={scId} onChange={(e) => setScId(e.target.value)}
                  placeholder={scApiOn ? "•••••• saved — paste to replace" : "client key from ConsumerDirect"} autoComplete="off" />
              </label>
              <label className="form-field">
                <span>Client Secret</span>
                <input type="password" value={scSecret} onChange={(e) => setScSecret(e.target.value)}
                  placeholder={scApiOn ? "•••••• saved — paste to replace" : "client secret from ConsumerDirect"} autoComplete="off" />
              </label>
            </div>
            <label className="form-field" style={{ maxWidth: 280, marginTop: 8 }}>
              <span>Environment</span>
              <select value={scEnv} onChange={(e) => setScEnv(e.target.value as "production" | "stage")}>
                <option value="production">Production (live keys)</option>
                <option value="stage">Stage / sandbox (test keys)</option>
              </select>
            </label>
            <p className="muted small" style={{ margin: "8px 0 10px" }}>
              Keys are issued per environment by ConsumerDirect (partnerintegration@consumerdirect.com), stored
              encrypted, and used only for your company. If Test says the IP isn't whitelisted, ask them to whitelist
              Cloudflare egress for your partner account.
            </p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="primary-button"
                disabled={scBusy || (!scId.trim() && !scSecret.trim() && scEnv === (s?.smartcredit_api_env || "production"))}
                onClick={saveSmartCredit}>
                {scBusy ? "Working…" : "Save"}
              </button>
              <button className="ghost-button" disabled={scBusy || !scApiOn} onClick={testSmartCredit}>
                Test connection
              </button>
            </div>
            {scMsg && <p className="muted small" style={{ marginTop: 10 }}>{scMsg}</p>}
          </div>

          {/* Credit-report acquisition status board */}
          <div className="panel">
            <div className="panel-header">
              <div>
                <h2>Credit-report import sources</h2>
                <span className="muted small">How a client's report gets into their file. Monitoring logins are set per client.</span>
              </div>
            </div>
            <div className="table-list">
              <StatusRow icon={ShieldCheck} title="iSoftpull soft-pull"
                detail={softpullOn ? "Connected — run a soft pull from any signed client's record." : "Connect your iSoftpull keys above to enable no-score-impact soft pulls."}
                status={softpullOn ? "connected" : "action"} />
              <StatusRow icon={Landmark} title="SmartCredit"
                detail={scApiOn ? "Partner API connected — imports use the official login-as session." : "Imports via the client's stored SmartCredit login (browser capture). Connect the Partner API above for the more reliable path."}
                status="connected" />
              <StatusRow icon={ScanLine} title="MyFreeScoreNow"
                detail="Imports from the client's stored MyFreeScoreNow login (set on the client record)."
                status="connected" />
              <StatusRow icon={ScanLine} title="IdentityIQ · MyScoreIQ"
                detail="Imports from the client's stored IDIQ login. MyScoreIQ reports real 3-bureau FICO (the rest report VantageScore)."
                status="connected" />
              <StatusRow icon={FileText} title="PDF upload"
                detail="Staff can upload a client's report saved as a text-based PDF (myFICO, Experian, annualcreditreport.com) — the text is parsed and analyzed like any other report."
                status="connected" />
              <StatusRow icon={Camera} title="Credit Karma capture"
                detail="Credit Karma blocks server-side login, so it's captured from your own logged-in browser via the capture extension and ingested into the client's file."
                status="off" />
            </div>
            <p className="muted small" style={{ margin: "10px 2px 0" }}>
              Per-client monitoring logins live on each client's record.{" "}
              <button className="linklike" onClick={() => nav("/clients")}>Open Clients</button>
            </p>
          </div>
        </>
      )}
    </Shell>
  );
}
